import { useEffect, useRef, useState } from "react"
import { ShieldCheck } from "lucide-react"
import { AdminAction, AdminDialog, AdminFeedback } from "@/features/cockpit/admin/ui"
import { useAuthStore } from "@/features/auth/useAuthStore"
import { setApprovalListener, takeApprovalContext, type ApprovalPrompt } from "./approval"

interface ApprovalInfo {
  state: string
  description?: string | null
  expires_in?: number | null
}

const ERROR_TEXT: Record<string, string> = {
  invalid_code: "Неверный код. Подождите следующий код в приложении и повторите.",
  invalid_code_format: "Код — это 6 цифр из приложения Google Authenticator.",
  admin_mode_locked: "Слишком много неверных кодов. Подождите 15 минут.",
  not_owner: "Это действие может подтвердить только владелец.",
  approval_unknown: "Запрос на подтверждение не найден или уже истёк.",
  broker_unavailable: "Служба подтверждений недоступна. Действие не выполнено.",
  admin_only: "Включите режим администрирования: напишите в чате «Джарвис, переходим в режим администрирования».",
}

async function call(method: string, path: string, body?: unknown) {
  const token = useAuthStore.getState().token
  const res = await fetch(`/api${path}`, {
    method,
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  const data = await res.json().catch(() => ({}))
  return { ok: res.ok, status: res.status, data }
}

function errorCode(data: unknown): string {
  const d = (data as { detail?: unknown })?.detail
  if (d && typeof d === "object" && "code" in d) return String((d as { code: unknown }).code)
  return typeof d === "string" ? d : "unknown"
}

/**
 * Ein Fenster für zwei Fälle:
 *  - "approval": konkrete Änderung bestätigen (zeigt, WAS ausgeführt wird, fragt den Code ab),
 *  - "enter_admin_mode": administrativen Modus mit dem Code einschalten.
 * Der Code wird nur an den Server gesendet, der ihn an den Broker weiterreicht; er steht in keinem Chat.
 */
export function ApprovalModal() {
  const [prompt, setPrompt] = useState<ApprovalPrompt | null>(null)
  const [ctx, setCtx] = useState("")
  const [info, setInfo] = useState<ApprovalInfo | null>(null)
  const [code, setCode] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const done = useRef<((ok: boolean) => void) | null>(null)

  useEffect(() => {
    setApprovalListener((p, cb) => {
      done.current?.(false) // ein älteres Fenster wird abgelehnt
      done.current = cb
      setPrompt(p)
      setCtx(takeApprovalContext())
      setInfo(null)
      setCode("")
      setError(null)
    })
    return () => setApprovalListener(null)
  }, [])

  useEffect(() => {
    if (prompt?.kind !== "approval") return
    let cancelled = false
    void call("GET", `/admin-mode/approvals/${prompt.approvalId}`).then((r) => {
      if (cancelled) return
      if (r.ok) setInfo(r.data as ApprovalInfo)
      else setError(ERROR_TEXT[errorCode(r.data)] ?? `Ошибка ${r.status}`)
    })
    return () => {
      cancelled = true
    }
  }, [prompt])

  if (!prompt) return null

  const finish = (ok: boolean) => {
    done.current?.(ok)
    done.current = null
    setPrompt(null)
  }

  const submit = async () => {
    setBusy(true)
    setError(null)
    const r =
      prompt.kind === "approval"
        ? await call("POST", `/admin-mode/approvals/${prompt.approvalId}/approve`, { code })
        : await call("POST", "/admin-mode/enter", { code })
    setBusy(false)
    if (r.ok) {
      finish(true)
      return
    }
    setCode("")
    setError(ERROR_TEXT[errorCode(r.data)] ?? `Ошибка ${r.status}`)
  }

  const isApproval = prompt.kind === "approval"
  return (
    <AdminDialog
      eyebrow="Безопасность"
      title={isApproval ? "Подтвердите действие" : "Режим администрирования"}
      icon={<ShieldCheck size={16} />}
      onClose={busy ? undefined : () => finish(false)}
      maxWidthClass="max-w-md"
      footer={
        <>
          <AdminAction onClick={() => finish(false)}>Отмена</AdminAction>
          <AdminAction tone="primary" onClick={submit}>
            {busy ? "Проверяю…" : isApproval ? "Подтвердить" : "Включить"}
          </AdminAction>
        </>
      }
    >
      <div className="space-y-3">
        {isApproval && ctx && (
          <div className="rounded-md border border-violet-500/30 bg-violet-500/10 p-3 text-sm text-[#e8eef8]">{ctx}</div>
        )}
        {isApproval && (
          <div className="rounded-md border border-[#2a3550] bg-[#101826] p-3 text-sm text-[#e8eef8]">
            <div className="text-xs uppercase tracking-wide text-[#8d9ab0]">Будет выполнено (сообщает брокер)</div>
            <div className="mt-1 break-words">{info?.description ?? "…"}</div>
            {typeof info?.expires_in === "number" && (
              <div className="mt-2 text-xs text-[#8d9ab0]">Запрос действует ещё {info.expires_in} с</div>
            )}
          </div>
        )}
        <p className="text-xs leading-relaxed text-[#8d9ab0]">
          Введите 6-значный код из Google Authenticator. Код вводится только здесь — не в чате. Одно подтверждение
          действует на это одно действие.
        </p>
        <input
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          onKeyDown={(e) => {
            if (e.key === "Enter" && code.length === 6 && !busy) void submit()
          }}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="000000"
          className="w-40 rounded-md border border-[#2a3550] bg-[#0b1220] px-3 py-2 text-center text-lg tracking-[0.4em] text-[#e8eef8]"
        />
        {error && <AdminFeedback tone="danger">{error}</AdminFeedback>}
      </div>
    </AdminDialog>
  )
}
